import React, { useState, useEffect } from 'react';
import { Clock, User, Receipt, RefreshCw, Search, ArrowLeft, Trash2, CircleAlert, SquarePen, Eye, Printer, MoreVertical } from 'lucide-react';
import Table from 'react-bootstrap/Table';
import Badge from 'react-bootstrap/Badge';
import Card from 'react-bootstrap/Card';
import Form from 'react-bootstrap/Form';
import Button from 'react-bootstrap/Button';
import InputGroup from 'react-bootstrap/InputGroup';
import Container from 'react-bootstrap/Container';
import Modal from 'react-bootstrap/Modal';
import Dropdown from 'react-bootstrap/Dropdown';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import FormControl from 'react-bootstrap/FormControl';
import style from './RecentOrdersPage.module.scss';
import Invoice from '../../components/invoice/Invoice';
import  getRecentOrders  from '../../services/order/getOrder';
import  deleteOrder  from '../../services/order/deleteOrders';
import  fetchProducts  from '../../services/products/fetchProducts';
import  updateOrder  from '../../services/order/updateOrder';
import printInvoices from '../../hooks/printInvoices';

const RecentOrdersPage = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [orderToDelete, setOrderToDelete] = useState(null);
  const [showOrderDetailsModal, setShowOrderDetailsModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingOrder, setEditingOrder] = useState(null);
  const [editableOrder, setEditableOrder] = useState(null);
  const [showAddItemsModal, setShowAddItemsModal] = useState(false);
  const [availableProducts, setAvailableProducts] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [selectedIds, setSelectedIds] = useState([]);
  const [productSearch, setProductSearch] = useState('');
  const [printOrder, setPrintOrder] = useState(null);

  const navigate = useNavigate();

  // Handle Enter key for delete confirmation
  useEffect(() => {
    if (!showDeleteModal) return;

    const handleKeyDown = (e) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleConfirmDelete();
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [showDeleteModal, orderToDelete]);

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const response = await getRecentOrders();
      setOrders(response.data);
    } catch (error) {
      toast.error('Failed to fetch orders');
      console.error('Error fetching orders:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEditClick = (order) => {
    setEditingOrder(order);
    setEditableOrder({
      ...order,
      items: order.items.map(item => ({ ...item }))
    });
    setShowEditModal(true);
  };

  const handleDeleteClick = (order) => {
    setOrderToDelete(order);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!orderToDelete) return;
    
    try {
      if (orderToDelete.isMultiple) {
        // Delete multiple orders
        const deletionPromises = selectedIds.map(id => deleteOrder(id));
        await Promise.all(deletionPromises);

        setOrders(prev => prev.filter(order => !selectedIds.includes(order._id)));
        setSelectedIds([]);
        toast.success(`${selectedIds.length} order${selectedIds.length > 1 ? 's' : ''} deleted successfully`);
      } else {
        // Delete single order
        await deleteOrder(orderToDelete._id);
        setOrders(prev => prev.filter(order => order._id !== orderToDelete._id));
        // Remove from selectedIds if it was selected
        setSelectedIds(prev => prev.filter(id => id !== orderToDelete._id));
        toast.success('Order deleted successfully');
        
        // Close order details modal if the deleted order was selected
        if (selectedOrder && selectedOrder._id === orderToDelete._id) {
          handleCloseOrderDetailsModal();
        }
      }
      
      setShowDeleteModal(false);
      setOrderToDelete(null);
    } catch (error) {
      toast.error('Failed to delete order(s)');
      console.error('Error deleting order:', error);
    }
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setOrderToDelete(null);
  };

  const handleRowClick = (order) => {
    setSelectedOrder(order);
    setShowOrderDetailsModal(true);
  };

  const handleCloseOrderDetailsModal = () => {
    setShowOrderDetailsModal(false);
    setSelectedOrder(null);
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setEditingOrder(null);
    setEditableOrder(null);
  };

  const handleOrderFieldChange = (field, value) => {
    setEditableOrder(prev => {
      const updated = {
        ...prev,
        [field]: value
      };

      // Table number is only relevant for dine-in and always 1
      if (field === 'orderType') {
        if (value === 'dine-in') {
          updated.tableNumber = 1;
        } else {
          updated.tableNumber = undefined;
        }
      }

      return updated;
    });
  };

  const handleItemChange = (index, field, value) => {
    setEditableOrder(prev => ({
      ...prev,
      items: prev.items.map((item, i) => 
        i === index ? { ...item, [field]: value } : item
      )
    }));
  };

  const handleDeleteItem = (index) => {
    setEditableOrder(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  const calculateTotal = () => {
    if (!editableOrder) return 0;
    return editableOrder.items.reduce((total, item) => 
      total + (item.quantity * item.price), 0
    );
  };

  const buildInvoiceDataFromOrder = (order) => {
    if (!order) {
      return {
        items: [],
        addedItemIds: [],
        itemQuantities: {},
        custDetails: {
          name: '',
          mobileNumber: '',
          paymentType: 'Cash',
          orderType: '',
          tableNumber: '',
          items: []
        },
        invoiceData: null
      };
    }

    const items = order.items.map((item, index) => ({
      _id: item.productId || index,
      name: item.productName || item.product?.name || `Item ${index + 1}`,
      price: item.price
    }));

    const addedItemIds = items.map(item => item._id);
    const itemQuantities = {};

    items.forEach((item, index) => {
      const sourceItem = order.items[index];
      itemQuantities[item._id] = sourceItem.quantity;
    });

    const custDetails = {
      name: order?.customerName || '',
      mobileNumber: order.mobileNumber || '',
      paymentType: order.paymentType || 'Cash',
      orderType: order.orderType,
      tableNumber: order.tableNumber,
      items: order.items
    };

    // Match billing's invoiceData structure
    const invoiceData = {
      data: {
        ...order,
        invoiceNumber: order.invoiceNumber,
        orderType: order.orderType,
        tableNumber: order.tableNumber,
        mobileNumber: order.mobileNumber,
        createdAt: order.createdAt,
        cashier: order.cashier,
        subtotal: order.subtotal,
        total: order.total,
        items: order.items
      }
    };

    return {
      items,
      addedItemIds,
      itemQuantities,
      custDetails,
      invoiceData
    };
  };

  const handlePrintOrderInvoice = async (order) => {
    if (!order) return;

    setPrintOrder(order);

    // Wait for hidden invoice DOM to render
    await new Promise(resolve => setTimeout(resolve, 250));
    await printInvoices('print-invoice-div');
    setPrintOrder(null);
  };

  const handleAddItemsClick = async () => {
    try {
      const response = await fetchProducts();
      setAvailableProducts(response.data || []);
      setShowAddItemsModal(true);
    } catch (error) {
      toast.error('Failed to fetch products');
      console.error('Error fetching products:', error);
    }
  };

  const handleCloseAddItemsModal = () => {
    setShowAddItemsModal(false);
    setAvailableProducts([]);
  };

  const handleAddItemToOrder = (product) => {
    const existingItemIndex = editableOrder.items.findIndex(
      item => item.productId === product._id || item.productName === product.name
    );
    
    if (existingItemIndex !== -1) {
      // Item exists, update quantity
      setEditableOrder(prev => ({
        ...prev,
        items: prev.items.map((item, index) => 
          index === existingItemIndex 
            ? { ...item, quantity: item.quantity + 1 }
            : item
        )
      }));
    } else {
      // Item doesn't exist, add new item
      const newItem = {
        productName: product.name,
        productId: product._id,
        quantity: 1,
        price: product.price
      };
      
      setEditableOrder(prev => ({
        ...prev,
        items: [...prev.items, newItem]
      }));
    }
  };

  const handleSaveOrder = async () => {
    if (!editableOrder || !editingOrder) return;

    console.log('=== SAVE ORDER DEBUG ===');
    console.log('Editing Order ID:', editingOrder._id);
    console.log('Editable Order:', editableOrder);

    // Validate quantities
    const invalidItems = editableOrder.items.filter(item => !item.quantity || item.quantity === '' || item.quantity < 1);
    if (invalidItems.length > 0) {
      toast.error('Please enter valid quantities for all items (minimum quantity: 1)');
      return;
    }

    try {
      setLoading(true);
      
      const updateData = {
        orderType: editableOrder.orderType,
        tableNumber: editableOrder.tableNumber,
        mobileNumber: editableOrder.mobileNumber,
        items: editableOrder.items
      };

      console.log('Update Data being sent:', updateData);

      const response = await updateOrder(editingOrder._id, updateData);
      
      console.log('Update Response:', response);
      
      // Build the updated order object
      const updatedOrder = {
        ...editingOrder,
        ...response.data,
      };

      // Update the order in the orders list
      setOrders(prev => prev.map(order => 
        order._id === editingOrder._id 
          ? updatedOrder
          : order
      ));

      toast.success('Order updated successfully');

      // Close edit modal and reopen details with fresh data
      setShowEditModal(false);
      setEditingOrder(null);
      setEditableOrder(null);
      setSelectedOrder(updatedOrder);
      setShowOrderDetailsModal(true);
    } catch (error) {
      console.error('Error updating order:', error);
      toast.error('Failed to update order');
    } finally {
      setLoading(false);
    }
  };

  // Checkbox selection functions
  const selectAllCheckBox = () => {
    if (allFilteredItemsSelected) {
      setSelectedIds([]);
    } else {
      const allFilteredIds = getFilteredOrders().map(order => order._id);
      setSelectedIds(allFilteredIds);
    }
  };

  const handleSingleSelect = (id) => {
    if (selectedIds.includes(id)) {
      setSelectedIds(prev => prev.filter(_id => _id !== id));
    } else {
      setSelectedIds(prev => [...prev, id]);
    }
  };

  const deleteMultipleOrders = async () => {
    if (selectedIds.length === 0) return;
    
    setOrderToDelete({ 
      _id: 'multiple', 
      invoiceNumber: `${selectedIds.length} order${selectedIds.length > 1 ? 's' : ''}`,
      isMultiple: true 
    });
    setShowDeleteModal(true);
  };

  // Filter orders for last 24 hours and by search term
  const getFilteredOrders = () => {
    const twentyFourHoursAgo = new Date(Date.now() - 24 * 60 * 60 * 1000);
    
    return orders.filter(order => {
      const isRecent = new Date(order.createdAt) >= twentyFourHoursAgo;
      const searchLower = searchTerm.toLowerCase();
      
      const matchesSearch = 
        order.invoiceNumber?.toLowerCase().includes(searchLower) ||
        order.tableNumber?.toLowerCase().includes(searchLower) ||
        order.mobileNumber?.toLowerCase().includes(searchLower) ||
        order.cashier?.name?.toLowerCase().includes(searchLower);
      
      return isRecent && matchesSearch;
    });
  };

  // Check if all filtered items are selected
  const allFilteredItemsSelected = getFilteredOrders().length > 0 && 
    getFilteredOrders().every(order => selectedIds.includes(order._id));

  // Pagination logic
  const getPaginatedOrders = () => {
    const filtered = getFilteredOrders();
    const indexOfLastOrder = currentPage * itemsPerPage;
    const indexOfFirstOrder = indexOfLastOrder - itemsPerPage;
    return filtered.slice(indexOfFirstOrder, indexOfLastOrder);
  };

  const totalPages = Math.ceil(getFilteredOrders().length / itemsPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  // Reset to page 1 when search term changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  const getStatusBadge = (status) => {
    const variants = {
      pending: 'warning',
      completed: 'success',
      cancelled: 'danger'
    };
    return <Badge bg={variants[status]} className='text-white'>{status}</Badge>;
  };

  const getOrderTypeBadge = (orderType) => {
    const variants = {
      'dine-in': 'primary',
      'take-away': 'info',
      'delivery': 'secondary'
    };
    return <Badge bg={variants[orderType]} className='text-white'>{orderType}</Badge>;
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-IN', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const filteredOrders = getFilteredOrders();

  return (
    <Container fluid className={style.recentOrdersPage}>
      {/* Hidden invoice container for printing from recent orders */}
      {printOrder && (() => {
        const {
          items,
          addedItemIds,
          itemQuantities,
          custDetails,
          invoiceData
        } = buildInvoiceDataFromOrder(printOrder);

        return invoiceData ? (
          <div id="invoice">
            <Invoice
              items={items}
              addedItemIds={addedItemIds}
              itemQuantities={itemQuantities}
              custDetails={custDetails}
              invoiceData={invoiceData}
            />
          </div>
        ) : null;
      })()}

      <Row className="mb-4">
        <Col>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <div>
              <h4 className="mb-1">Recent Orders</h4>
              <p className="text-muted mb-0">
                Orders from the last 24 hours
              </p>
            </div>
          </div>
          
          {/* Search Bar */}
          <div className={`${style.SearchButton} d-flex flex-row gap-2 align-items-center justify-content-between`}>
            <InputGroup className={`${style.searchBox}`}>
              <InputGroup.Text><Search size={16} /></InputGroup.Text>
              <FormControl
                placeholder="Search by invoice, table, phone, or cashier..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </InputGroup>
            
            {selectedIds.length > 0 && (
              <div 
                className={`${style.selectedTrash}`} 
                onClick={deleteMultipleOrders}
                title={`Delete ${selectedIds.length} selected order${selectedIds.length > 1 ? 's' : ''}`}
              >
                <Trash2 size={20} />
              </div>
            )}
          </div>
        </Col>
      </Row>

      <Row>
        <Col lg={12}>
          <Card className={style.categoriesCard}>
            <Card.Body>
              <div className="table-responsive">
                <Table hover className={style.categoriesTable}>
                  <thead>
                    <tr>
                      <th>
                        <Form.Check 
                          type="checkbox"
                          checked={allFilteredItemsSelected}
                          onChange={selectAllCheckBox}
                          id={`select-all-checkbox`}
                        />
                      </th>
                      <th className="text-center">SL/NO</th>
                      <th className='text-center'>Time</th>
                      <th className='text-center'>Invoice #</th>
                      <th className="text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan="6" className="text-center p-4">
                          <RefreshCw size={24} className="spin" />
                          <p className="mt-2">Loading orders...</p>
                        </td>
                      </tr>
                    ) : getPaginatedOrders().length > 0 ? (
                      getPaginatedOrders().map((order, index) => (
                        <tr key={order._id}>
                          <td>
                            <Form.Check 
                              type="checkbox"
                              checked={selectedIds.includes(order._id)}
                              onChange={() => handleSingleSelect(order._id)}
                              onClick={(e) => e.stopPropagation()}
                              id={`checkbox-${order._id}`}
                            />
                          </td>
                          <td className='text-center fw-bold'>{filteredOrders.length - ((currentPage - 1) * itemsPerPage + index)}</td>
                          <td
                            className={`text-center ${style.clickableCell}`}
                            onClick={() => handleRowClick(order)}
                          >
                            <div className={style.timeInfo}>
                              <div className="fw-bold">{formatTime(order.createdAt)}</div>
                              <small className="text-muted">{formatDate(order.createdAt)}</small>
                            </div>
                          </td>
                          <td
                            className={`text-center ${style.clickableCell}`}
                            onClick={() => handleRowClick(order)}
                          >
                            <span className="fw-bold">{order.invoiceNumber || '-'}</span>
                          </td>
                          <td className="text-center">
                            <Button 
                              variant="outline-danger" 
                              size="sm"
                              onClick={() => handleDeleteClick(order)}
                            >
                              <Trash2 size={14} />
                            </Button>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="6" className="text-center p-4 text-muted">
                          <Receipt size={48} className="mb-3" />
                          <h5>No recent orders found</h5>
                          <p>Orders from the last 24 hours will appear here</p>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>
            </Card.Body>
          </Card>

          {/* Pagination Controls */}
          {totalPages > 1 && (
            <div className="d-flex justify-content-center align-items-center gap-3 mt-3">
              <Button
                variant="outline-secondary"
                size="sm"
                disabled={currentPage === 1}
                onClick={() => handlePageChange(currentPage - 1)}
              >
                &laquo;
              </Button>

              <span>
                Page <strong>{currentPage}</strong> of {totalPages}
              </span>

              <Button
                variant="outline-secondary"
                size="sm"
                disabled={currentPage === totalPages}
                onClick={() => handlePageChange(currentPage + 1)}
              >
                &raquo;
              </Button>
            </div>
          )}
        </Col>
      </Row>

      {/* Delete Confirmation Modal */}
      <Modal
        show={showDeleteModal}
        onHide={handleCloseDeleteModal}
        size="lg"
        aria-labelledby="contained-modal-title-vcenter"
        centered
      >
        <Modal.Body className="d-flex flex-column align-items-center text-center">
          <h5 className="mb-3">Are you sure you want to delete this order?</h5>
          
          <div className="mb-4">
            <CircleAlert size={48} className="text-danger" />
          </div>
          <div className="d-flex justify-content-center gap-3">
            <Button variant="secondary" onClick={handleCloseDeleteModal}>No</Button>
            <Button variant="danger" onClick={handleConfirmDelete}>Yes</Button>
          </div>
        </Modal.Body>
      </Modal>

      {/* Order Details Modal */}
      <Modal
        show={showOrderDetailsModal}
        onHide={handleCloseOrderDetailsModal}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Order Details</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{height:"75vh", overflowX: "auto"}} className='OrderDetailsModal'>
          {selectedOrder && (
            <div>
              <div className="d-flex justify-content-end mb-3">
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={() => handleEditClick(selectedOrder)}
                >
                  <SquarePen size={14} className="me-1" />
                  Edit
                </Button>
              </div>
              <div className="row mb-3">
                <div className="col-md-6">
                  <h6>Order Information</h6>
                  <table className="table table-sm">
                    <tr>
                      <td><strong>Invoice #:</strong></td>
                      <td>{selectedOrder.invoiceNumber || 'N/A'}</td>
                    </tr>
                    <tr>
                      <td><strong>Order Type:</strong></td>
                      <td>{getOrderTypeBadge(selectedOrder.orderType)}</td>
                    </tr>
                    <tr>
                      <td><strong>Table:</strong></td>
                      <td>{selectedOrder.tableNumber || 'N/A'}</td>
                    </tr>
                  </table>
                </div>
                <div className="col-md-6">
                  <h6>Customer & Cashier</h6>
                  <table className="table table-sm">
                    <tr>
                      <td><strong>Mobile:</strong></td>
                      <td>{selectedOrder.mobileNumber || 'N/A'}</td>
                    </tr>
                    <tr>
                      <td><strong>Cashier:</strong></td>
                      <td>
                        <div className="d-flex align-items-center gap-1">
                          <User size={14} />
                          {selectedOrder.cashier?.name || 'N/A'}
                        </div>
                      </td>
                    </tr>
                    <tr>
                      <td><strong>Date:</strong></td>
                      <td>{formatDate(selectedOrder.createdAt)}</td>
                    </tr>
                    <tr>
                      <td><strong>Time:</strong></td>
                      <td>{formatTime(selectedOrder.createdAt)}</td>
                    </tr>
                  </table>
                </div>
              </div>

              <div className="mb-3">
                <h6>Order Items</h6>
                <Table striped bordered hover size="sm">
                  <thead>
                    <tr>
                      <th>Product</th>
                      <th>Quantity</th>
                      <th>Price</th>
                      <th>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedOrder.items.map((item, index) => (
                      <tr key={index}>
                        <td className={style.orderDetailsProductCell}>{item.productName || item.product?.name}</td>
                        <td>{item.quantity}</td>
                        <td>₹{item.price}</td>
                        <td>₹{(item.quantity * item.price).toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>

              <div className="text-end">
                <h5>
                  <strong>Total: ₹{selectedOrder.subtotal}</strong>
                </h5>
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          {selectedOrder && (
            <div className="d-flex justify-content-end gap-2 w-100">
              <Button
                variant="outline-danger"
                onClick={() => handleDeleteClick(selectedOrder)}
              >
               <Trash2 size={18}/> Delete
              </Button>
              <Button
                variant="outline-primary"
                onClick={() => handlePrintOrderInvoice(selectedOrder)}
              >
               <Printer size={18}/> Print
              </Button>
            </div>
          )}
        </Modal.Footer>
      </Modal>

      {/* Edit Order Modal */}
      <Modal
        show={showEditModal}
        onHide={handleCloseEditModal}
        size="xl"
        centered
        backdrop="static"
        backdropClassName="modal-backdrop-blur"
      >
        <Modal.Header closeButton className={style.editOrderHeader}>
          <div>
            <Modal.Title className="mb-1">Edit Invoice</Modal.Title>
          </div>
        </Modal.Header>
        <Modal.Body style={{height:"75vh", overflowX: "auto"}} className={`${style.editOrderModalBody} modal-body`}>
          {editableOrder && (
            <div
              className={`${style.editOrderContent} ${
                showAddItemsModal ? style.editOrderDimmed : ''
              }`}
            >
              <div className="row mb-3">
                <div className="col-md-6">
                  <h6 className={style.sectionTitle}>Order Information</h6>
                  <table className={`table table-sm ${style.infoTable}`}>
                    <tr>
                      <td><strong>Invoice #:</strong></td>
                      <td>{editableOrder.invoiceNumber || 'N/A'}</td>
                    </tr>
                    <tr>
                      <td style={{ whiteSpace: 'nowrap' }}><strong>Order Type:</strong></td>
                      <td>
                        <Form.Select 
                          className={style.compactField}
                          value={editableOrder.orderType || 'dine-in'}
                          onChange={(e) => handleOrderFieldChange('orderType', e.target.value)}
                          size="sm"
                          style={{ maxWidth: '150px' }}
                        >
                          <option value="dine-in">Dine-In</option>
                          <option value="take-away">Take-Away</option>
                        </Form.Select>
                      </td>
                    </tr>
                    {editableOrder.orderType === 'dine-in' && (
                      <tr>
                        <td><strong>Table:</strong></td>
                        <td>
                          <span className="fw-semibold">1</span>
                        </td>
                      </tr>
                    )}
                  </table>
                </div>
                <div className="col-md-6">
                  <h6 className={style.sectionTitle}>Customer & Cashier</h6>
                  <table className={`table table-sm ${style.infoTable}`}>
                    {process.env.REACT_APP_ADD_CUSTOMER === 'true' && (
                    <tr>
                      <td><strong>Mobile:</strong></td>
                      <td>
                        <Form.Control
                          type="text"
                          className={style.compactField}
                          value={editableOrder.mobileNumber || ''}
                          onChange={(e) => handleOrderFieldChange('mobileNumber', e.target.value)}
                          placeholder="Enter mobile number"
                          size="sm"
                          style={{ maxWidth: '150px' }}
                        />
                      </td>
                    </tr>
                  )}
                    <tr>
                      <td><strong>Cashier:</strong></td>
                      <td>
                        <div className="d-flex align-items-center gap-1">
                          <User size={14} />
                          {editableOrder.cashier?.name || 'N/A'}
                        </div>
                      </td>
                    </tr>
                    <tr>
                      <td><strong>Date:</strong></td>
                      <td>{formatDate(editableOrder.createdAt)}</td>
                    </tr>
                    <tr>
                      <td><strong>Time:</strong></td>
                      <td>{formatTime(editableOrder.createdAt)}</td>
                    </tr>
                  </table>
                </div>
              </div>

              <div className="mb-3">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <h6 className={`${style.sectionTitle} mb-0`}>Order Items</h6>
                  <Button variant="primary" size="sm" onClick={handleAddItemsClick}>
                    Add Items
                  </Button>
                </div>
                <div className={style.itemsTableWrapper}>
                  <Table striped bordered hover size="sm" className={style.itemsTable}>
                    <thead>
                      <tr>
                        <th>Product</th>
                        <th className="text-center">Quantity</th>
                        <th className="text-end">Price</th>
                        <th className="text-end">Total</th>
                        <th className="text-center">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {editableOrder.items.map((item, index) => (
                        <tr key={index}>
                          <td className={style.productCell}>{item.productName || item.product?.name || ''}</td>
                          <td className={`text-center ${style.quantityCell}`}>
                            <Form.Control
                              type="number"
                              size="sm"
                              value={item.quantity}
                              min="1"
                              onChange={(e) => {
                                const value = e.target.value;
                                handleItemChange(index, 'quantity', value === '' ? '' : Number(value));
                              }}
                            />
                          </td>
                          <td className="text-end">₹{item.price || 0}</td>
                          <td className="text-end">₹{(item.quantity * item.price).toFixed(2)}</td>
                          <td className="text-center">
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => handleDeleteItem(index)}
                            >
                              <Trash2 size={14} />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              </div>

              <div className={style.orderSummary}>
                <div className="text-end">
                  <p className="mb-1 text-muted small">Grand Total</p>
                  <h5 className="mb-0">
                    <strong>₹{calculateTotal().toFixed(2)}</strong>
                  </h5>
                </div>
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <div className="d-flex justify-content-end w-100">
            <div className="gap-2 d-flex">
              <Button variant="secondary" onClick={handleCloseEditModal}>
                Cancel
              </Button>
              <Button variant="success" onClick={handleSaveOrder} disabled={loading}>
                {loading ? 'Saving...' : 'Save'}
              </Button>
            </div>
          </div>
        </Modal.Footer>
      </Modal>
      {/* Add Items Modal */}
      <Modal
        show={showAddItemsModal}
        onHide={handleCloseAddItemsModal}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Add Items to Order</Modal.Title>
        </Modal.Header>
        <Modal.Body className={style.addItemsModalBody} style={{height: "75vh", overflowX:"auto"}}>
          <div className="mb-3">
            <InputGroup className={style.productSearch}>
              <InputGroup.Text><Search size={16} /></InputGroup.Text>
              <FormControl
                placeholder="Search products..."
                value={productSearch}
                onChange={(e) => setProductSearch(e.target.value)}
              />
            </InputGroup>
          </div>
          
          <div className={style.productsTableWrapper} style={{height: "60vh"}}>
            <Table striped hover size="sm" className={style.productsTable}>
              <thead>
                <tr>
                  <th>Product Name</th>
                  <th className="text-center">Price</th>
                  <th className="text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {availableProducts
                  .filter((product) =>
                    product.name.toLowerCase().includes(productSearch.toLowerCase())
                  )
                  .map((product) => (
                    <tr key={product._id}>
                      <td className={style.productCell}>{product.name}</td>
                      <td className="text-center">₹{product.price}</td>
                      <td className="text-center">
                        <Button
                          variant="success"
                          size="sm"
                          className={style.addItemButton}
                          onClick={() => handleAddItemToOrder(product)}
                        >
                          Add
                        </Button>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </Table>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseAddItemsModal}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default RecentOrdersPage;
